import React, { useEffect, useState } from 'react';
import { Form, Input, Radio, Button, message } from 'antd';
import { EyeTwoTone, EyeInvisibleOutlined } from '@ant-design/icons';
import { useHeaderChanger } from 'hooks/useHeaderChanger';
import { PageFormBox } from 'ui/PageFormBox';
import { FormWrapper } from 'ui/FormWrapper';
import { FormSection } from 'ui/FormSection';
import { SectionTitle } from 'ui/SectionTitle';
import { requiredRule } from 'utils/forms/validationRules';
import { FETCHING_STATUS, REQUEST_LOADING } from 'constants/alerts';
import { IntegrationService } from 'services/IntegrationService';
import { BCC_INTEGRATION } from 'constants/integration';
import { AccessControl } from 'components/AccessControl';
import { CONTENT_SPAN, LABEL_SPAN } from 'constants/table';
import { useDocumentTitle } from 'hooks/useDocumentTitle';

function BCC() {
  useDocumentTitle('Настройки интеграции с БЦК');
  useHeaderChanger('Настройки интеграции с БЦК');
  const [config, setConfig] = useState(null);
  const [form] = Form.useForm();

  useEffect(() => {
    fetchConfig();
  }, []);

  useEffect(() => {
    if (config) {
      const { bcc_global, bcc_fin, bcc_pay, bcc_info } = config;
      form.setFieldsValue({
        login: bcc_global?.login,
        pwd: bcc_global?.pwd,
        sign_id: bcc_global?.sign_id,
        client_id: bcc_global?.client_id,
        secret_id: bcc_global?.secret_id,
        fin_api_enable: bcc_fin?.fin_api_enable,
        fin_api_send_url: bcc_fin?.fin_api_send_url,
        fin_api_auth_url: bcc_fin?.fin_api_auth_url,
        pay_api_enable: bcc_pay?.pay_api_enable,
        pay_api_send_url: bcc_pay?.pay_api_send_url,
        pay_api_auth_url: bcc_pay?.pay_api_auth_url,
        info_api_enable: bcc_info?.info_api_enable,
        info_api_send_url: bcc_info?.info_api_send_url,
        info_api_auth_url: bcc_info?.info_api_auth_url,
      });
    }
  }, [config]);

  const fetchConfig = async () => {
    const hide = message.loading(FETCHING_STATUS);

    try {
      const { items } = await IntegrationService.getConfig(BCC_INTEGRATION);
      setConfig(items);
    } catch (err) {
      message.error('Не удалось загрузить данные');
    } finally {
      hide();
    }
  };

  const handleSubmit = async (values) => {
    const {
      login,
      pwd,
      sign_id,
      client_id,
      secret_id,
      fin_api_enable,
      fin_api_send_url,
      fin_api_auth_url,
      pay_api_enable,
      pay_api_send_url,
      pay_api_auth_url,
      info_api_enable,
      info_api_send_url,
      info_api_auth_url,
    } = values;

    const payload = {
      bcc_global: {
        login,
        pwd,
        sign_id,
        client_id,
        secret_id,
      },
      bcc_fin: {
        fin_api_enable,
        fin_api_send_url,
        fin_api_auth_url,
      },
      bcc_pay: {
        pay_api_enable,
        pay_api_send_url,
        pay_api_auth_url,
      },
      bcc_info: {
        info_api_enable,
        info_api_send_url,
        info_api_auth_url,
      },
    };

    const hide = message.loading(REQUEST_LOADING);

    try {
      const isSuccess = await IntegrationService.saveConfig(
        BCC_INTEGRATION,
        payload
      );

      if (isSuccess) {
        message.success('Успешно сохранено');
      }
    } catch (err) {
      message.error('Не удалось сохранить данные');
    } finally {
      hide();
    }
  };

  return (
    <PageFormBox>
      <FormWrapper style={{ padding: '0 30px' }}>
        <Form
          form={form}
          layout="horizontal"
          labelCol={{
            span: LABEL_SPAN,
          }}
          wrapperCol={{ span: CONTENT_SPAN }}
          labelAlign="right"
          onFinish={handleSubmit}
        >
          <FormSection>
            <SectionTitle>
              Глобальные настройки для интеграции с АО БЦК
            </SectionTitle>

            <Form.Item name="login" label="Логин" rules={[requiredRule]}>
              <Input placeholder="Введите логин" />
            </Form.Item>

            <Form.Item name="pwd" label="Пароль" rules={[requiredRule]}>
              <Input.Password
                placeholder="Введите пароль"
                iconRender={(visible) =>
                  visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />
                }
              />
            </Form.Item>

            <Form.Item
              name="sign_id"
              label="Серийный номер ЭЦП"
              rules={[requiredRule]}
            >
              <Input placeholder="Введите номер" />
            </Form.Item>

            <Form.Item
              name="client_id"
              label="Идентификатор клиента"
              rules={[requiredRule]}
            >
              <Input placeholder="Введите идентификатор" />
            </Form.Item>

            <Form.Item
              name="secret_id"
              label="Секрет ID"
              rules={[requiredRule]}
            >
              <Input placeholder="Введите ID" />
            </Form.Item>
          </FormSection>

          <FormSection>
            <SectionTitle>Financial API</SectionTitle>

            <Form.Item
              name="fin_api_enable"
              label="Интеграция"
              rules={[requiredRule]}
            >
              <Radio.Group>
                <Radio value="1">Включить</Radio>
                <Radio value="0">Выключить</Radio>
              </Radio.Group>
            </Form.Item>

            <Form.Item
              name="fin_api_send_url"
              label="Точки подключения"
              rules={[requiredRule]}
            >
              <Input placeholder="Введите URL" />
            </Form.Item>

            <Form.Item
              name="fin_api_auth_url"
              label="Авторизация"
              rules={[requiredRule]}
            >
              <Input placeholder="Введите URL" />
            </Form.Item>
          </FormSection>

          <FormSection>
            <SectionTitle>Payment gateway</SectionTitle>

            <Form.Item
              name="pay_api_enable"
              label="Интеграция"
              rules={[requiredRule]}
            >
              <Radio.Group>
                <Radio value="1">Включить</Radio>
                <Radio value="0">Выключить</Radio>
              </Radio.Group>
            </Form.Item>

            <Form.Item
              name="pay_api_send_url"
              label="Точки подключения"
              rules={[requiredRule]}
            >
              <Input placeholder="Введите URL" />
            </Form.Item>

            <Form.Item
              name="pay_api_auth_url"
              label="Авторизация"
              rules={[requiredRule]}
            >
              <Input placeholder="Введите URL" />
            </Form.Item>
          </FormSection>

          <FormSection>
            <SectionTitle>Informational API</SectionTitle>

            <Form.Item
              name="info_api_enable"
              label="Интеграция"
              rules={[requiredRule]}
            >
              <Radio.Group>
                <Radio value="1">Включить</Radio>
                <Radio value="0">Выключить</Radio>
              </Radio.Group>
            </Form.Item>

            <Form.Item
              name="info_api_send_url"
              label="Точки подключения"
              rules={[requiredRule]}
            >
              <Input placeholder="Введите URL" />
            </Form.Item>

            <Form.Item
              name="info_api_auth_url"
              label="Авторизация"
              rules={[requiredRule]}
            >
              <Input placeholder="Введите URL" />
            </Form.Item>

            <AccessControl pageId="IntegrationConfig" action="update">
              <Form.Item wrapperCol={{ offset: CONTENT_SPAN }}>
                <Button type="primary" htmlType="submit">
                  Сохранить
                </Button>
              </Form.Item>
            </AccessControl>
          </FormSection>
        </Form>
      </FormWrapper>
    </PageFormBox>
  );
}

export { BCC };
